import { Box, Modal, Title } from "@mantine/core";
import { useDisclosure, useHover } from "@mantine/hooks";
import { IconSettings } from "@tabler/icons-react";
import type { JSX } from "react";

import ActionButton from "../ActionButton";
import AiPersonalityForm, { type IFormValues } from "../aiPersonalities/AiPersonalityForm";
import type { Language, ReasoningEffortType, Verbosity } from "../utils";
import classes from "./Personality.module.css";

interface IProps {
    aiPersonality: {
        name: string;
        externalName: string | null;
        temperature: number | null;
        systemPrompt: string;
        includeRetrieval: boolean;
        includeBrainstorm: boolean;
        includeMissingInformation: boolean;
        epsilon: number;
        tags: string[];
        languages: Language[];
        model: string;
        useCache: boolean;
        reasoningEffort: ReasoningEffortType | null;
        responseFormat: string | null;
        verbosity: Verbosity | null;
        maxIterations: number;
    };
    updatePersonality: (values: IFormValues) => void;
}

function Personality({ aiPersonality, updatePersonality }: IProps): JSX.Element {
    const [opened, { open, close }] = useDisclosure(false);
    const { hovered, ref } = useHover();

    const save = (values: IFormValues) => {
        updatePersonality(values);
        close();
    };

    const initialValues = {
        name: aiPersonality.name,
        externalName: aiPersonality.externalName,
        systemPrompt: aiPersonality.systemPrompt,
        temperature: aiPersonality.temperature,
        includeRetrieval: aiPersonality.includeRetrieval,
        includeBrainstorm: aiPersonality.includeBrainstorm,
        includeMissingInformation: aiPersonality.includeMissingInformation,
        epsilon: aiPersonality.epsilon,
        tags: aiPersonality.tags,
        languages: aiPersonality.languages,
        model: aiPersonality.model,
        useCache: aiPersonality.useCache,
        showInChat: false,
        reasoningEffort: aiPersonality.reasoningEffort,
        verbosity: aiPersonality.verbosity,
        useResponseFormat: !!aiPersonality.responseFormat,
        responseFormat: aiPersonality.responseFormat,
        maxIterations: aiPersonality.maxIterations,
    };

    return (
        <Box className={classes.box} ref={ref}>
            <Title order={3} size="md">
                AI personality: {aiPersonality.name}
            </Title>
            {hovered && (
                <ActionButton
                    onClick={open}
                    label="Tweak AI personality"
                    icon={<IconSettings stroke={1.5} />}
                    className={classes.setting}
                />
            )}
            <Modal opened={opened} onClose={close} title="Tweak AI personality" size="xl">
                <AiPersonalityForm
                    handleSave={save}
                    initialValues={initialValues}
                    fetching={false}
                    hideName
                    hideShowInChat
                />
            </Modal>
        </Box>
    );
}

export default Personality;
